/**
 * Stacked / grouped bar chart by SDR (rep).
 *
 * Replicates the cl-intranet/sales/ dashboard look: one bar per SDR, stacked
 * segments per category. Pure SVG so no extra dependency.
 *
 * Categories[] is the order from BOTTOM to TOP of each stack.
 *
 * Each segment has a `label` (the value the click handler receives, e.g. a raw
 * call outcome used to filter the drill-down) and an optional `display` (what
 * the chart shows). They are separate so translating what is shown never
 * changes what is sent.
 */

export interface BarSeries {
  rep: string;
  total: number;
  segments: Array<{ label: string; value: number; color: string; display?: string }>;
}

interface Props {
  data: BarSeries[];
  yLabels?: number[];
  width?: number;
  height?: number;
  /** Optional click handler so each bar segment is interactive. */
  onSegmentClick?: (rep: string, segmentLabel: string) => void;
  /** When true, show segment legend below the chart. */
  showLegend?: boolean;
  /** When true, render segments side-by-side (grouped) instead of stacked. */
  grouped?: boolean;
}

export function BarsByRep({ data, width = 320, height = 200, onSegmentClick, showLegend = true, grouped = false }: Props) {
  if (data.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>Sin datos.</div>;
  }

  const padding = { top: 16, right: 12, bottom: 40, left: 36 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const allSegmentLabels: string[] = [];
  for (const d of data) for (const s of d.segments) if (!allSegmentLabels.includes(s.label)) allSegmentLabels.push(s.label);
  const segmentColors = new Map<string, string>();
  for (const d of data) for (const s of d.segments) if (!segmentColors.has(s.label)) segmentColors.set(s.label, s.color);
  const segmentDisplay = new Map<string, string>();
  for (const d of data) for (const s of d.segments) if (!segmentDisplay.has(s.label)) segmentDisplay.set(s.label, s.display ?? s.label);

  const maxValue = (() => {
    if (grouped) {
      let m = 0;
      for (const d of data) for (const s of d.segments) m = Math.max(m, s.value);
      return m;
    }
    let m = 0;
    for (const d of data) m = Math.max(m, d.total);
    return m;
  })();
  const niceMax = Math.max(1, Math.ceil(maxValue * 1.1));

  const barGroupWidth = chartW / data.length;
  const barWidth = grouped
    ? Math.max(4, (barGroupWidth * 0.7) / Math.max(1, allSegmentLabels.length))
    : Math.min(48, barGroupWidth * 0.6);

  const yScale = (v: number): number => chartH - (v / niceMax) * chartH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => Math.round(niceMax * p));

  return (
    <div style={{ width: '100%' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" style={{ display: 'block' }}>
        {/* Y axis ticks + grid */}
        {ticks.map((t) => {
          const y = padding.top + yScale(t);
          return (
            <g key={t}>
              <line x1={padding.left} x2={padding.left + chartW} y1={y} y2={y} stroke="var(--rule)" strokeWidth={1} />
              <text x={padding.left - 6} y={y + 3} textAnchor="end" fontSize={9} fill="var(--fg-subtle)">{t}</text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const groupX = padding.left + i * barGroupWidth + barGroupWidth / 2;
          const segs = d.segments;
          if (grouped) {
            const totalW = barWidth * segs.length;
            return (
              <g key={d.rep}>
                {segs.map((s, j) => {
                  const x = groupX - totalW / 2 + j * barWidth;
                  const h = (s.value / niceMax) * chartH;
                  const y = padding.top + chartH - h;
                  return (
                    <rect
                      key={s.label}
                      x={x} y={y} width={barWidth - 1} height={Math.max(0, h)}
                      fill={s.color}
                      onClick={onSegmentClick ? (e) => { e.stopPropagation(); onSegmentClick(d.rep, s.label); } : undefined}
                      style={{ cursor: onSegmentClick ? 'pointer' : 'default', pointerEvents: 'all' }}
                    >
                      <title>{`${d.rep} · ${s.display ?? s.label}: ${s.value}`}</title>
                    </rect>
                  );
                })}
                <text x={groupX} y={padding.top + chartH + 14} textAnchor="middle" fontSize={10} fill="var(--fg-muted)">{d.rep}</text>
              </g>
            );
          }
          // stacked: el total de cada persona encima de su barra (se lee sin pasar el raton)
          let acc = 0;
          const cima = padding.top + chartH - (d.total / niceMax) * chartH;
          return (
            <g key={d.rep}>
              {d.total > 0 && <text x={groupX} y={Math.max(10, cima - 5)} textAnchor="middle" fontSize={10} fontWeight={600} fill="var(--fg)">{d.total}</text>}
              {segs.map((s) => {
                const h = (s.value / niceMax) * chartH;
                const y = padding.top + chartH - (acc + s.value) / niceMax * chartH;
                acc += s.value;
                return (
                  <rect
                    key={s.label}
                    x={groupX - barWidth / 2} y={y} width={barWidth} height={Math.max(0, h)}
                    fill={s.color}
                    onClick={onSegmentClick ? (e) => { e.stopPropagation(); onSegmentClick(d.rep, s.label); } : undefined}
                    style={{ cursor: onSegmentClick ? 'pointer' : 'default', pointerEvents: 'all' }}
                  >
                    <title>{`${d.rep} · ${s.display ?? s.label}: ${s.value}`}</title>
                  </rect>
                );
              })}
              <text x={groupX} y={padding.top + chartH + 14} textAnchor="middle" fontSize={10} fill="var(--fg-muted)">{d.rep}</text>
            </g>
          );
        })}
      </svg>

      {showLegend && allSegmentLabels.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 6, fontSize: 10, color: 'var(--fg-muted)' }}>
          {allSegmentLabels.map((l) => (
            <span
              key={l}
              onClick={onSegmentClick ? (e) => { e.stopPropagation(); onSegmentClick(data[0]?.rep ?? '', l); } : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                cursor: onSegmentClick ? 'pointer' : 'default',
                padding: '2px 6px', borderRadius: 4,
                transition: 'background 100ms',
              }}
              onMouseEnter={(e) => { if (onSegmentClick) e.currentTarget.style.background = 'var(--surface-hi)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ width: 8, height: 8, background: segmentColors.get(l), display: 'inline-block', borderRadius: 2 }} />
              {segmentDisplay.get(l) ?? l}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
